import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout1 from "../../Layouts/Layout1";
import LayoutContainer from "../../Layouts/LayoutContainer";
import {
  ChevronLeft,
  ChevronRight,
  Info,
  HelpCircle,
  Check,
} from "lucide-react";
import RentalDataUseModal from "./RentalDataUseModal";
import { useRentalProfile } from "../../context/RentalProfileContext";
import {
  isRenterProfileComplete,
  isSectionComplete,
  RENTAL_PROFILE_SECTIONS,
} from "../../Constants/rentalProfile";
import { downloadRenterProfilePdf } from "../../utils/downloadRenterProfilePdf";
import ProfileDownloadCard from "./ProfileDownloadCard";
import { toast } from "react-toastify";

const personalItems = [
  "Personal details",
  "About me",
  "Address history",
  "Employment",
  "Finances",
  "Identity documents",
];

const householdItems = ["People", "Pets"];

const SectionList = ({ items, onItemClick, isComplete }) => (
  <div className="rounded-xl border border-[#E5E7EB] bg-white shadow-sm overflow-hidden">
    {items.map((label, i) => (
      <button
        key={label}
        type="button"
        onClick={() => onItemClick?.(label)}
        className={`flex w-full items-center justify-between gap-4 px-4 py-4 text-left text-[15px] font-medium text-[#1A1A1A] transition-colors hover:bg-[#F9FAFB] ${
          i < items.length - 1 ? "border-b border-[#E5E7EB]" : ""
        }`}
      >
        <span className="flex items-center gap-2">
          {isComplete?.(label) ? (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#10B981] text-white">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
          ) : null}
          {label}
        </span>
        <ChevronRight
          className="h-5 w-5 shrink-0 text-[#097FE6]"
          strokeWidth={2}
        />
      </button>
    ))}
  </div>
);

const PERSONAL_SECTION_KEYS = {
  "Personal details": RENTAL_PROFILE_SECTIONS.PERSONAL_DETAILS,
  "About me": RENTAL_PROFILE_SECTIONS.ABOUT_ME,
  "Address history": RENTAL_PROFILE_SECTIONS.ADDRESS_HISTORY,
  Employment: RENTAL_PROFILE_SECTIONS.EMPLOYMENT,
  Finances: RENTAL_PROFILE_SECTIONS.FINANCES,
  "Identity documents": RENTAL_PROFILE_SECTIONS.IDENTITY_DOCUMENTS,
};

const HOUSEHOLD_SECTION_KEYS = {
  People: RENTAL_PROFILE_SECTIONS.HOUSEHOLD_PEOPLE,
  Pets: RENTAL_PROFILE_SECTIONS.HOUSEHOLD_PETS,
};

const RentalApplication = () => {
  const navigate = useNavigate();
  const { profile, loading } = useRentalProfile();
  const [dataUseModalOpen, setDataUseModalOpen] = useState(false);

  const isPersonalComplete = (label) =>
    !loading && isSectionComplete(PERSONAL_SECTION_KEYS[label], profile);

  const isHouseholdComplete = (label) =>
    !loading && isSectionComplete(HOUSEHOLD_SECTION_KEYS[label], profile);

  const profileComplete = useMemo(
    () => !loading && isRenterProfileComplete(profile),
    [loading, profile]
  );

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await downloadRenterProfilePdf(profile);
      toast.success("Renter profile PDF downloaded.");
    } catch {
      toast.error("Could not generate PDF. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePersonalItemClick = (label) => {
    const routes = {
      "Personal details": "/rental-applications/personal-details",
      "About me": "/rental-applications/about-me",
      "Address history": "/rental-applications/address-history",
      Employment: "/rental-applications/employment",
      Finances: "/rental-applications/finances",
      "Identity documents": "/rental-applications/identity-documents",
    };
    const path = routes[label];
    if (path) navigate(path);
  };

  const handleHouseholdItemClick = (label) => {
    const routes = {
      People: "/rental-applications/household/people",
      Pets: "/rental-applications/household/pets",
    };
    const path = routes[label];
    if (path) navigate(path);
  };

  return (
    <Layout1>
      <LayoutContainer className="max-w-7xl py-8 md:py-12 lg:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-[#097FE6] hover:underline"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={2} />
              Back to overview
            </button>

            <div className="mb-8 flex gap-3 rounded-xl bg-[#EBF5FF] px-4 py-3.5 md:px-5 md:py-4">
              <Info
                className="mt-0.5 h-5 w-5 shrink-0 text-[#097FE6]"
                strokeWidth={2}
              />
              <p className="text-sm leading-relaxed text-[#1A1A1A]">
                For your security, send enquiries via realestate.com.au,
                don&apos;t send private information over email and attend an
                inspection before paying funds.
              </p>
            </div>

            <h1 className="text-2xl font-bold tracking-tight text-[#000929] md:text-3xl">
              Renter Profile
            </h1>
            <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-[#6B7280]">
              Create your Renter Profile once and reuse it for all your
              applications.
            </p>

            <section className="mt-10">
              <h2 className="text-lg font-semibold text-[#1A1A1A]">Personal</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-[#6B7280]">
                Details to help property managers validate who you are and
                assess your identity, employment and income.
              </p>
              <div className="mt-4">
                <SectionList
                  items={personalItems}
                  onItemClick={handlePersonalItemClick}
                  isComplete={isPersonalComplete}
                />
              </div>
            </section>

            <section className="mt-10">
              <h2 className="text-lg font-semibold text-[#1A1A1A]">
                Household
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-[#6B7280]">
                Details of who you&apos;ll live with, pets &amp; utility
                preferences. Adding &apos;People&apos; and &apos;Pets&apos; here
                allows you to easily add them to any future application.
              </p>
              <div className="mt-4">
                <SectionList
                  items={householdItems}
                  onItemClick={handleHouseholdItemClick}
                  isComplete={isHouseholdComplete}
                />
              </div>
            </section>

            {profileComplete ? (
              <ProfileDownloadCard
                onDownload={handleDownloadPdf}
                downloading={downloadingPdf}
              />
            ) : null}

            <button
              type="button"
              onClick={() => setDataUseModalOpen(true)}
              className="mt-10 inline-flex items-center gap-2 text-sm text-[#6B7280] underline decoration-[#6B7280] underline-offset-2 hover:text-[#1A1A1A]"
            >
              <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
              How this information is used
            </button>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-sm lg:sticky lg:top-24">
              <div className="space-y-5">
                <div>
                  <h3 className="text-base font-semibold text-[#1A1A1A]">
                    Manage applications
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">
                    <a
                      href="#"
                      className="font-medium text-[#097FE6] hover:underline"
                      onClick={(e) => e.preventDefault()}
                    >
                      Manage and view rental applications
                    </a>{" "}
                    you&apos;ve already started.
                  </p>
                </div>

                <div className="border-t border-[#E5E7EB] pt-5">
                  <h3 className="text-base font-semibold text-[#1A1A1A]">
                    How long we keep your data
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">
                    To keep your Renter Profile data up to date (and, more
                    secure), we delete certain information if not used for
                    renters in South Australia.{" "}
                    <a
                      href="#"
                      className="font-medium text-[#097FE6] hover:underline"
                      onClick={(e) => e.preventDefault()}
                    >
                      Learn more in our help centre.
                    </a>
                  </p>
                </div>

                <div className="border-t border-[#E5E7EB] pt-5">
                  <h3 className="text-base font-semibold text-[#1A1A1A]">
                    Need help?
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">
                    Search and find answers in our{" "}
                    <a
                      href="#"
                      className="font-medium text-[#097FE6] hover:underline"
                      onClick={(e) => e.preventDefault()}
                    >
                      help centre
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </LayoutContainer>

      <RentalDataUseModal
        open={dataUseModalOpen}
        onClose={() => setDataUseModalOpen(false)}
      />
    </Layout1>
  );
};

export default RentalApplication;
